import {
  Box,
  ButtonProps,
  CircularProgress,
  CircularProgressProps,
  SvgIconProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button as MuiButton, TablePagination, IconButton,
} from '@mui/material';
import React, {ChangeEvent, useMemo, useState} from 'react';
import '../../../../scss/components/_list-header.scss';
import {SignatureStatus} from '../../models/signature-tracking';
import {
  Status,
  Button,
  LoadingOverlay,
} from '@finpay-development/shared-components';
import {
  StatusColors,
} from '@finpay-development/shared-components/lib/esm/utils/enums';
import {authDocDispatchMethod, authDocType} from '../../../../shared/enums';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import {patientService} from '../../../services/patient-service';
import {
  showErrorStatus,
  showStatus,
} from '../../../../security/state/user-slice';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch} from '../../../../shared/state/store';
import {format} from 'date-fns';
import _ from 'lodash';
import AccessControl from '../../../../security/components/access-control';
import {RolePageNames} from '../../../../security/model/role-page-names';
import AddIcon from '@mui/icons-material/Add';
import DocumentsModal from './documents-modal';
import DeleteIcon from '@mui/icons-material/Delete';
import {DocumentDeleteModal} from './document-delete-modal';
import {
  createPatientDocument,
  deletePatientDocument,
} from '../../../state/patient-thunk';
import {RootState} from '../../../../shared/state/root-reducer';

export type PatientDocument = {
  patientDocumentId: number
  patientId: number
  patientEncounterId: number
  storageProvider: 's3' | 'dropbox'
  storageMetadata: Record<string, any>
  user: {
    firstName: string
    lastName: string
  }
  authorizationDocumentStatus?: {
    authorizationDocumentStatusId: number
    patientChampionId: number | null
    documentStatus: 'sent' | 'created' | 'signed'
    paymentId: number
    dateApproved: 'string'
    dateSent: 'string'
    authorizationDocuments: {
      authorizationDocumentsId: number
      typeOfDoc: authDocType
      documentUrl: string
      clientId: number
      clientFacilityId: number
      isSigReq: boolean
    }
  }
}

interface DocumentAccordionContentsProps {
  documents: PatientDocument[];
  patientId: number;
  patientEncounterId: number;
}

const buttonProps: ButtonProps = {
  variant: 'outlined',
  color: 'inherit',
  sx: {
    height: '3rem',
    borderWidth: '2px',
    borderColor: '#828282',
    minWidth: '5rem',
    boxShadow: 3,
    margin: '0.5rem 1rem 0 0',
  },
};

const iconProps: SvgIconProps = {
  fontSize: 'small',
  color: 'inherit',
};

const circularProgressProps: CircularProgressProps = {
  size: 24,
  style: {
    left: '1.3rem',
    position: 'absolute',
    marginTop: '0.5rem',
  },
};

function formatDocumentDate(value?: string | null): string {
  if (_.isNil(value) || value === '') {
    return '';
  }
  return format(value, 'MM/dd/yyyy');
}

const cellSx = {
  date: {
    width: '15%',
    whiteSpace: 'nowrap',
  },
  docName: {
    width: '25%',
    minWidth: 220,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  status: {
    width: '10%',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  uploadedBy: {
    width: '15%',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  resend: {
    width: '20%',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  delete: {
    width: '5%',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },

};

export const DocumentsAccordionContentsV2 = (props: DocumentAccordionContentsProps) => {

  const {
    documents = [],
    patientId,
    patientEncounterId,
  } = props;

  const dispatch = useDispatch<AppDispatch>();

  const stateFields = {
    loading: useSelector((state: RootState) => {
      return state.patientContext.isLoading;
    }),
  };

  const {loading} = stateFields;

  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [isDocumentModelOpen, setDocumentModalOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectDocumentId, setSelectedDocumentId] = useState<number>(0);

  const handleButtonOnClick = async (
      docType: authDocType, dispatchMethod: authDocDispatchMethod) => {

    let apiResponse;

    switch (docType) {
      case authDocType.inst:
        apiResponse = await patientService.installmentDocument(
            patientEncounterId, dispatchMethod);
        break;
      case authDocType.spa:
        apiResponse = await patientService.spaDocument(patientEncounterId,
            dispatchMethod);
        break;
    }

    if (apiResponse?.hasErrors) {
      dispatch(showErrorStatus(
          apiResponse?.entity?.message || apiResponse.errorMessage));
    } else {
      dispatch(showStatus('Authorization document sent successfully'));
    }

  };

  const tableBody = useMemo(() => {
    return (
        <TableBody>
          {
            loading.patientDocuments ? (
                <TableRow>
                  <TableCell
                      colSpan={7}
                      sx={{
                        position: 'relative',
                        border: 0,
                        minHeight: 240,
                        verticalAlign: 'middle',
                        textAlign: 'center',
                      }}
                  >
                    <LoadingOverlay whiteBackground={true}/>
                  </TableCell>
                </TableRow>
            ) : (

                documents.slice(
                    page * rowsPerPage,
                    page * rowsPerPage +
                    rowsPerPage,
                ).map((document) => {

                  const isAuthDoc = document.authorizationDocumentStatus;

                  return (
                      <TableRow key={document.patientDocumentId}>
                        <TableCell
                            align="center"
                            sx={{py: 3, ...cellSx.date}}
                        >
                          <Typography variant="h4">
                            {formatDocumentDate(
                                document.authorizationDocumentStatus?.dateSent,
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{py: 3, ...cellSx.date}}
                        >
                          <Typography variant="h4">
                            {formatDocumentDate(
                                document.authorizationDocumentStatus?.dateApproved,
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{py: 3, ...cellSx.docName}}
                        >
                          <Typography variant="h4">
                            {
                              isAuthDoc ?
                                  getDocumentName(
                                      document.authorizationDocumentStatus?.authorizationDocuments?.typeOfDoc) :
                                  document.storageProvider === 's3' ?
                                      document.storageMetadata.key.split('/').
                                          pop() :
                                      'N/A'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                              verticalAlign: 'middle',
                              py: 3,
                              ...cellSx.status,
                            }}
                        >
                          {isAuthDoc ? (
                              <Box
                                  sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                              >
                                <Status
                                    text={_.capitalize(
                                        document.authorizationDocumentStatus!.documentStatus)}
                                    statusColor={
                                      document.authorizationDocumentStatus!.documentStatus ===
                                      SignatureStatus.signed
                                          ?
                                          StatusColors.success
                                          :
                                          document.authorizationDocumentStatus!.documentStatus ===
                                          SignatureStatus.sent
                                              ? StatusColors.warning
                                              : StatusColors.error
                                    }
                                />
                              </Box>
                          ) : null}
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                              py: 3,
                              ...cellSx.uploadedBy,
                            }}
                        >
                          <Typography variant="h4">
                            {
                              //Setting N/A for documents sent out automatically.
                              isAuthDoc ?
                                  'N/A' :
                                  `${_.capitalize(
                                      document.user.firstName)} ${_.capitalize(
                                      document.user.lastName)}`

                            }
                          </Typography>
                        </TableCell>
                        <TableCell align="center"
                                   sx={{py: 3, ...cellSx.resend}}>
                          {document.authorizationDocumentStatus?.authorizationDocuments?.isSigReq ===
                          false &&
                          document.authorizationDocumentStatus?.documentStatus !==
                          'signed' ? (
                              <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                              >
                                <MuiButton
                                    {...buttonProps}
                                    disabled={isSendingEmail}
                                    onClick={async () => {
                                      try {
                                        setIsSendingEmail(true);
                                        await handleButtonOnClick(
                                            document.authorizationDocumentStatus!.authorizationDocuments.typeOfDoc,
                                            authDocDispatchMethod.email);
                                      } finally {
                                        setIsSendingEmail(false);
                                      }
                                    }}
                                >
                                  <EmailOutlinedIcon {...iconProps} />
                                  {isSendingEmail && (
                                      <CircularProgress {...circularProgressProps} />
                                  )}
                                </MuiButton>
                                <MuiButton
                                    {...buttonProps}
                                    disabled={isSendingSms}
                                    onClick={async () => {
                                      try {
                                        setIsSendingSms(true);
                                        await handleButtonOnClick(
                                            document.authorizationDocumentStatus!.authorizationDocuments.typeOfDoc,
                                            authDocDispatchMethod.sms);
                                      } finally {
                                        setIsSendingSms(false);
                                      }
                                    }}
                                >
                                  <ChatBubbleIcon {...iconProps} />
                                  {isSendingSms && (
                                      <CircularProgress {...circularProgressProps} />
                                  )}
                                </MuiButton>
                              </Box>
                          ) : null}
                        </TableCell>
                        <TableCell align="center"
                                   sx={{py: 3, ...cellSx.delete}}>
                          {!isAuthDoc && (
                              <Box
                                  sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                              >
                                <IconButton
                                    aria-haspopup="true"
                                    onClick={() => {
                                      setSelectedDocumentId(
                                          document.patientDocumentId);
                                      setIsOpen(true);
                                    }}
                                    size="large">
                                  <DeleteIcon/>
                                </IconButton>
                              </Box>
                          )}
                        </TableCell>
                      </TableRow>
                  );
                })
            )
          }
        </TableBody>
    );

  }, [
    documents,
    page,
    rowsPerPage,
    isSendingEmail,
    isSendingSms,
    loading.patientDocuments]);

  return (
      <div className="list-header documents-accordion px-4 pb-4 pt-2">
        <TableContainer sx={{mt: 1, mb: 1}}>
          <Table
              size="small"
              sx={{tableLayout: 'fixed', width: '100%'}}
          >
            <TableHead>
              <TableRow className="header px-1 mb-1">
                <TableCell align="center" sx={{py: 2, ...cellSx.date}}>
                  <Typography
                      variant="h4">
                    Date Sent
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{py: 2, ...cellSx.date}}>
                  <Typography variant="h4">
                    Date Signed
                  </Typography>
                </TableCell>
                <TableCell
                    align="center"
                    sx={{py: 2, ...cellSx.docName}}
                >
                  <Typography
                      variant="h4"
                  >
                    Document Name
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{py: 2, ...cellSx.status}}>
                  <Typography
                      variant="h4"
                  >
                    Status
                  </Typography>
                </TableCell>
                <TableCell
                    align="center"
                    sx={{py: 2, ...cellSx.uploadedBy}}
                >
                  <Typography variant="h4">Uploaded By</Typography>
                </TableCell>
                <TableCell align="center" sx={{py: 2, ...cellSx.resend}}>
                  <Typography variant="h4">Re-Send</Typography>
                </TableCell>
                <TableCell align="center" sx={{py: 2, ...cellSx.delete}}>
                </TableCell>
              </TableRow>
            </TableHead>
            {tableBody}
          </Table>
          <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                width: '100%',
              }}
          >
            <TablePagination
                component="div"
                rowsPerPageOptions={[
                  5,
                  10,
                  15,
                ]}
                count={Array.isArray(documents) ? documents.length : 0}
                rowsPerPage={rowsPerPage}
                page={page}
                labelRowsPerPage={'View'}
                onPageChange={(
                    event: unknown, newPage: number) => {
                  setPage(newPage);
                }}
                onRowsPerPageChange={(
                    event: ChangeEvent<HTMLInputElement>,
                ) => {
                  setRowsPerPage(+event.target.value);
                  setPage(0);
                }}
            />
          </Box>
        </TableContainer>
        <AccessControl
            rolePageName={RolePageNames.PatientRecords}
            actionName="Add Instance of Care"
            renderNoAccess={() => <Button disabled>Add Document</Button>}
        >
          <Button
              type="text"
              icon={<AddIcon/>}
              paddingLeft={0}
              marginTop={4}
              onClick={() => {
                setDocumentModalOpen(true);
              }}
          >
            Add Document
          </Button>
        </AccessControl>

        <DocumentsModal
            patientID={patientId}
            encounterId={patientEncounterId}
            open={isDocumentModelOpen}
            onClose={() => {
              setDocumentModalOpen(false);
            }}
            onDocumentUpload={async (formValues) => {
              const abortController = new AbortController();
              await dispatch(createPatientDocument({
                patientId: formValues.patientID,
                encounterId: formValues.encounterId,
                file: formValues.file,
                cancelSignal: abortController.signal,
              }));
            }}
        />
        {
            isOpen && (
                <DocumentDeleteModal
                    selectedDocumentId={selectDocumentId}
                    setIsOpen={setIsOpen}
                    deleteOnClick={async () => {
                      const abortController = new AbortController();
                      dispatch(deletePatientDocument({
                        encounterId: patientEncounterId,
                        patientDocumentId: selectDocumentId,
                        cancelSignal: abortController.signal,
                      }));
                      setIsOpen(false);
                    }}
                />
            )
        }

      </div>
  );
};

const getDocumentName = (typeOfDoc?: string) => {
  switch (typeOfDoc) {
    case 'inst' :
      return 'Installment Agreement';
    case 'spa' :
      return 'Single Pay Agreement';
    case 'pfr' :
      return 'PFR Adjustment/Refinance Agreement';
    case 'phi' :
      return 'PHI Consent';
    case 'otp' :
      return 'One Time Payment Agreement';
    default :
      return '';
  }
};